import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { communicationsAPI, claimsAPI } from '../services/api';

const Communications = () => {
  const { reference } = useParams(); // Get claim reference from URL if present
  const [communications, setCommunications] = useState([]);
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState([]);
  const [lightboxComm, setLightboxComm] = useState(null);

  useEffect(() => {
    loadCommunications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  const loadCommunications = async () => {
    try {
      if (reference) {
        // If viewing claim-specific communications
        const claimResponse = await claimsAPI.getById(reference);
        setClaim(claimResponse.data);

        const response = await claimsAPI.getCommunications(reference);
        setCommunications(response.data || response || []);
      } else {
        // If viewing all communications
        const response = await communicationsAPI.getAll();
        setCommunications(response.data || response || []);
      }
    } catch (error) {
      console.error('Error loading communications:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const openLightbox = (comm) => {
    setLightboxComm(comm);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxComm(null);
    document.body.style.overflow = 'auto';
  };

  // Helper function to decode HTML entities (like &nbsp; &amp; etc)
  const decodeHtmlEntities = (text) => {
    if (!text) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  const getPreview = (content, type) => {
    if (!content) return '';

    // For emails, don't show any preview (subject only)
    if (type === 'email') {
      return '';
    }

    // For SMS and other types, show 2-3 lines
    // Strip HTML tags for preview
    let strippedContent = content.replace(/<[^>]*>/g, '');

    // Decode HTML entities for non-email types (like &nbsp;)
    if (type === 'sms' || type === 'letter') {
      strippedContent = decodeHtmlEntities(strippedContent);
    }

    // Split into lines and take first 3
    const lines = strippedContent.split('\n').filter(line => line.trim());
    const preview = lines.slice(0, 3).join('\n');

    // Limit to ~150 characters total
    if (preview.length > 150) {
      return preview.substring(0, 150) + '...';
    }

    return preview;
  };

  // Helper to render content based on type
  const renderContent = (comm) => {
    if (comm.type === 'email' && comm.content && comm.content.includes('<')) {
      // Email with HTML content - preserve all original spacing and formatting
      return (
        <div
          style={{
            color: '#666',
            lineHeight: 'inherit'
          }}
          className="email-content"
          dangerouslySetInnerHTML={{ __html: comm.content }}
        />
      );
    } else {
      // SMS, letter, or plain text - decode HTML entities
      const decodedContent = decodeHtmlEntities(comm.content || 'No content');
      return <p style={{ margin: 0, color: '#666', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{decodedContent}</p>;
    }
  };

  if (loading) return <Layout><div>Loading...</div></Layout>;

  return (
    <Layout>
      <style>{`
        .email-content {
          overflow-wrap: break-word;
          word-wrap: break-word;
        }
        .email-content p {
          margin: 1em 0 !important;
        }
        .email-content p:first-child {
          margin-top: 0 !important;
        }
        .email-content p:last-child {
          margin-bottom: 0 !important;
        }
        .email-content br {
          display: block;
          content: "";
          margin: 0.5em 0;
        }
        .email-content table {
          border-spacing: 0;
          margin: 1em 0;
        }
        .email-content td {
          padding: 0.25em 0;
        }
      `}</style>

      {reference && claim && (
        <div style={{ marginBottom: '2rem', fontSize: '0.9rem', color: '#666' }}>
          <Link to="/claims" style={{ color: '#4A90E2', textDecoration: 'none' }}>
            My Claims
          </Link>
          {' '}&gt;{' '}
          <Link to={`/claims/${reference}`} style={{ color: '#4A90E2', textDecoration: 'none' }}>
            {claim.claim_reference}
          </Link>
          {' '}&gt;{' '}
          <span>Communications</span>
        </div>
      )}

      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 style={{ margin: 0 }}>
          {reference ? `Communications for ${claim?.claim_reference || 'Claim'}` : 'Communications'}
        </h2>
        <div style={{ fontSize: '0.9rem', color: '#666' }}>
          {communications.length} message{communications.length !== 1 ? 's' : ''}
        </div>
      </div>

      {communications.length === 0 ? (
        <div style={{ background: 'white', padding: '2rem', borderRadius: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
          <p style={{ color: '#666' }}>No communications found.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {communications.map((comm) => {
            const isExpanded = expandedIds.includes(comm.id);
            const preview = getPreview(comm.content, comm.type);

            return (
              <div key={comm.id} style={{ background: 'white', borderRadius: '8px', border: '1px solid #ddd', overflow: 'hidden', transition: 'box-shadow 0.2s' }}>
                <div style={{ padding: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '1rem', marginBottom: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          background: comm.type === 'email' ? '#4A90E2' : comm.type === 'sms' ? '#2ecc71' : '#e74c3c',
                          color: 'white',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          textTransform: 'uppercase'
                        }}>
                          {comm.type}
                        </span>
                        <span style={{ fontSize: '0.85rem', color: '#888' }}>
                          {new Date(comm.sent_date).toLocaleDateString()} at {new Date(comm.sent_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      {comm.subject && (
                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', color: '#2c3e50' }}>
                          {comm.subject}
                        </h4>
                      )}
                    </div>
                  </div>

                  {!isExpanded && preview && (
                    <p style={{ margin: '0 0 0.75rem 0', color: '#666', fontSize: '0.9rem', lineHeight: '1.5', whiteSpace: 'pre-line' }}>
                      {preview}
                    </p>
                  )}

                  {isExpanded && (
                    <div style={{ margin: '0.75rem 0', padding: '1rem', background: '#f8f9fa', borderRadius: '6px', maxHeight: '400px', overflowY: 'auto' }}>
                      {renderContent(comm)}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => toggleExpand(comm.id)}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#4A90E2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        transition: 'background 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.background = '#357ABD'}
                      onMouseOut={(e) => e.target.style.background = '#4A90E2'}
                    >
                      {isExpanded ? '▲ Collapse' : '▼ Expand'}
                    </button>
                    <button
                      onClick={() => openLightbox(comm)}
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'white',
                        color: '#4A90E2',
                        border: '1px solid #4A90E2',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.background = '#4A90E2';
                        e.target.style.color = 'white';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = 'white';
                        e.target.style.color = '#4A90E2';
                      }}
                    >
                      🔍 View Full
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxComm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={closeLightbox}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #e0e0e0',
              background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
              color: 'white'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '1rem' }}>
                <div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      {lightboxComm.type}
                    </span>
                  </div>
                  {lightboxComm.subject && (
                    <h3 style={{ margin: '0.5rem 0 0 0', fontSize: '1.25rem' }}>
                      {lightboxComm.subject}
                    </h3>
                  )}
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
                    Sent: {new Date(lightboxComm.sent_date).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={closeLightbox}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    color: 'white',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                  onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Content */}
            <div style={{
              padding: '1.5rem',
              overflowY: 'auto',
              flex: 1,
              lineHeight: 'inherit'
            }}>
              {lightboxComm.type === 'email' && lightboxComm.content && lightboxComm.content.includes('<') ? (
                <div className="email-content" dangerouslySetInnerHTML={{ __html: lightboxComm.content }} />
              ) : (
                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{decodeHtmlEntities(lightboxComm.content || 'No content')}</p>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid #e0e0e0',
              background: '#f8f9fa',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={closeLightbox}
                style={{
                  padding: '0.5rem 1.5rem',
                  background: '#4A90E2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#357ABD'}
                onMouseOut={(e) => e.target.style.background = '#4A90E2'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Communications;
